/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { t_rec_arr, t_key } from '../types';

import is_protected from '../is/is_protected';
import is_record from '../is/is_record';
import is_array from '../is/is_array';
import fold from './fold';

type t_source_fn<T> = (v: unknown, k: t_key, root: T, pointer: string) => unknown | undefined;

/**
 * @private
 */
function __s<T>(pointer: string, node_fn: t_source_fn<T>, leaf_fn: t_source_fn<T>, root: T): T {
  if (root == null) {
    return root;
  }

  return fold(
    (_root, v, k) => {
      if (is_protected(`${k}`)) {
        return _root;
      }

      const _pointer = `${pointer}/${k}`;

      if (is_record(v) || is_array(v)) {
        _root[k] = node_fn(v, k, _root, _pointer) ?? v;
        __s(_pointer, node_fn, leaf_fn, v as T);
      } else {
        _root[k] = leaf_fn(v, k, _root, _pointer) ?? v;
      }

      return _root;
    },
    root,
    root as t_rec_arr<unknown>
  );
}

export interface Source {
  /**
   * Given a sequence, map over numbers
   *
   * @public
   * @function f1.source
   * @param node_fn a function to map over each node
   * @param leaf_fn a function to map over each leaf
   * @param root root of tree
   * @example
   *
   * ```ts
   * f1.source(f1.skip, (leaf, key) => ({
   *   ...leaf,
   *   title: f1.title(leaf.name)
   * }), {
   *   'a': {
   *     title: 'lower case title',
   *     friends: [{
   *       title: 'another lower case title',
   *     }]
   *   }
   * }) =>> {
   *   'a': {
   *     title: 'Lower Case Title',
   *     friends: [{
   *       title: 'Another Lower Case Title',
   *     }]
   *   }
   * }
   * ```
   */
  <T>(node_fn: t_source_fn<T>, leaf_fn: t_source_fn<T>, root: T): T;
  <T>(node_fn: t_source_fn<T>, leaf_fn: t_source_fn<T>): (root: T) => T;
}

/**
 * @see Source
 */
export default <T>(node_fn: t_source_fn<T>, leaf_fn: t_source_fn<T>, root: T): T => __s('', node_fn, leaf_fn, root);
